// --- Begin Updated Code for PublicationPage.js ---
//
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { useJobs } from '../context/jobContext';
import { useLocation } from 'react-router-dom';
import './PublicationPage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faStopCircle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import { formatMarkdownContent } from '../utils/HelperFunctions';
//import AiSummaryModal from "./PublicationSummaryModal"; // Import the new modal
import MarketFitDashboardModal from './MarketFitDashboardModal'; // Import the modal
import ContentQualityDashboardModal from './ContentQualityDashboardModal';
import Spinner from './Spinner';
import ErrorDisplayModal from './ErrorDisplayModal';
import './ErrorDisplayModal.module.css';

// Example channels
const CHANNEL_OPTIONS = [
  { key: 'youtube_public', label: 'YouTube Public' },
  { key: 'youtube_private', label: 'YouTube Private' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'educational', label: 'Educational' },
  { key: 'blog', label: 'Blog' },
  { key: 'vk', label: 'VK' },
  { key: 'other', label: 'Other' },
];

// --- NEW: Configuration for unimplemented platforms ---
const UNIMPLEMENTED_PLATFORMS = ['instagram', 'tiktok', 'facebook', 'vk'];

const getTierClassName = (tier) => {
  switch (tier?.toLowerCase()) {
    case 'prime':
    case 'excellent':
    case 'excellent fit':
      return 'prime';
    case 'promising':
    case 'good':
    case 'high fit':
    case 'high':
      return 'promising';
    case 'moderate fit':
    case 'moderate':
    case 'acceptable':
      return 'acceptable';
    case 'low fit':
    case 'low':
    case 'mid-risk':
      return 'midRisk'; // Note: Matches the CSS class .midRisk
    case 'high-risk':
    case 'poor':
    case 'poor fit':
    case 'not acceptable':
      return 'highRisk'; // Note: Matches the CSS class .highRisk
    default:
      return '';
  }
};

const formatUrlForHref = (url) => {
  if (
    !url ||
    typeof url !== 'string' ||
    url.trim() === '' ||
    url.trim() === '#'
  ) {
    return '#';
  }
  const trimmedUrl = url.trim();
  // Check if it already has a common protocol or is a protocol-relative URL
  if (/^(https?:\/\/|mailto:|tel:|\/\/)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }
  // If not, prepend "https://"
  return `https://${trimmedUrl}`;
};

const formatDuration = (totalSeconds) => {
  if (!totalSeconds || isNaN(totalSeconds)) {
    return 'N/A';
  }
  const secondsInt = Math.floor(totalSeconds);
  const hours = Math.floor(secondsInt / 3600);
  const minutes = Math.floor((secondsInt % 3600) / 60);
  const seconds = secondsInt % 60;

  const paddedHours = String(hours).padStart(2, '0');
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
};

// This component centralizes the rendering of job status, warnings, and errors.
const JobStatusPanel = ({ jobState, onDismissError, onCancel }) => {
  // 1. Render nothing if the job is idle or successfully finished with no warnings.
  if (
    !jobState.jobStatus ||
    jobState.jobStatus === 'idle' ||
    (jobState.jobStatus === 'finished' &&
      (!jobState.results?.warnings || jobState.results.warnings.length === 0))
  ) {
    return null;
  }

  // Check if this is a terminal failure
  const isHardFailure = jobState.jobStatus === 'failed';

  // 2. Render spinner and progress message for pending/starting states.
  if (jobState.jobStatus === 'pending' || jobState.jobStatus === 'starting') {
    return (
      <div className="job-status-panel loading">
        <Spinner size="small" inline={true} />
        <span>{jobState.message || 'Analysis in progress...'}</span>
        <p>
          You can navigate away from this page; we'll notify you when it's
          complete.
        </p>
        {/* --- NEW CANCEL BUTTON --- */}
        <button
          onClick={(e) => {
            e.preventDefault();
            onCancel();
          }}
          className="cancel-job-button"
        >
          <FontAwesomeIcon icon={faStopCircle} /> Stop Analysis
        </button>
      </div>
    );
  }

  // 3. Collect all messages (job-level error + warnings) to display.
  const messagesToDisplay = [];
  // 1. Check for specific job errors (only if not promoted to Global)
  if (jobState.error) {
    const msg =
      typeof jobState.error === 'object'
        ? jobState.error.message
        : String(jobState.error);
    messagesToDisplay.push(msg);
  }

  // 2. Add warnings from results
  if (jobState.results?.warnings?.length > 0) {
    messagesToDisplay.push(...jobState.results.warnings);
  }

  if (messagesToDisplay.length > 0) {
    return (
      <div
        className={`job-status-panel ${isHardFailure ? 'error' : 'warning'}`}
      >
        <div className="message-content">
          {/* Use 'Failed' header for hard failures */}
          <h3>
            {isHardFailure ? 'Analysis Failed:' : 'Analysis Notifications:'}
          </h3>
          <ul>
            {messagesToDisplay.map((msg, index) => (
              <li key={index}>{msg}</li>
            ))}
          </ul>
        </div>
        <button onClick={onDismissError} className="dismiss-error-button">
          &times;
        </button>
      </div>
    );
  }
  return null;
};

const PublicationPage = () => {
  const {
    // --- Core Influencer & App State ---
    // The currently active influencer and the main query results list.
    queryResults,
    activeinfluencerId,
    setActiveinfluencerId,
    isLoadingQueryData,

    // --- Publication Data & Actions ---
    // State for the list of publications and the functions to manage them.
    publications,
    setPublications,
    fetchPublicationsByInfluencer,
    createPublication,
    deletePublication,
    refreshPublicationMetadata,

    // Products Data & Actions
    // State for the list of products and the functions to manage them.
    fetchProductsToPublish,
    togglePublishedProduct,
    toggleIncludeVideoData,
    productToggles,
    setProductToggles,

    // --- AI Summary Actions (for individual publications) ---
    // Functions related to generating and managing AI-powered summaries.
    generatePublicationAISummary,
    fetchPublicationAiSummary,
    generatePrivatePublicationAISummary,

    // --- Market Fit & Aggregated Analysis ---
    // State for aggregated/individual analysis results and the functions to fetch/clear them.
    aggregatedMarketFit,
    aggregatedPublicationSummary,
    individualMarketFitAnalysis,
    fetchAggregatedMarketFit,
    fetchAggregatedPublicationSummary,
    fetchIndividualMarketFitAnalysis,
    generateSingleMarketFitAnalysis,
    clearAggregatedData,

    // --- General State Management ---
    // App-wide loading and error states.
    //loading,
    error,
    setError,
    clearError,
  } = useData();

  const {
    contentAnalysisJob,
    setContentAnalysisJobState,
    startContentAnalysisJob,
    clearContentAnalysisJobState,
    clearContentAnalysisJobError,
    cancelContentAnalysisJob,
  } = useJobs();

  const location = useLocation(); // Get location

  // Local states
  const [isSaving, setIsSaving] = useState(false); // State for Spinner control
  const [isLoadingPage, setIsLoadingPage] = useState(true); // State for Spinner control
  const [spinnerMessage, setSpinnerMessage] = useState('');
  const [forceReanalysis, setForceReanalysis] = useState(false);
  const [selectedMarketFitData, setSelectedMarketFitData] = useState(null);
  const [isMarketFitModalOpen, setIsMarketFitModalOpen] = useState(false);
  const [isAggregatedMarketFitModalOpen, setIsAggregatedMarketFitModalOpen] =
    useState(false);
  const [selectedContentQualityData, setSelectedContentQualityData] =
    useState(null);
  const [isContentQualityModalOpen, setIsContentQualityModalOpen] =
    useState(false);
  const [
    isAggregatedContentQualityModalOpen,
    setIsAggregatedContentQualityModalOpen,
  ] = useState(false);
  // --- NEW: Local state for Agent results ---
  const [agentJobState, setAgentJobState] = useState(null);

  // Local states for handling publications
  const [newUrl, setNewUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newChannel, setNewChannel] = useState(CHANNEL_OPTIONS[0].key);

  // --- 1. Deriving state from context ---

  // We create a "Merged" influencer object.
  // Priority 1: Global Context (if loaded and complete)
  // Priority 2: Location State (passed from Modal)
  const activeInfluencer = useMemo(() => {
    // Try to find in global store
    const fromContext = (queryResults || []).find(
      (item) => item.influencerId === activeinfluencerId,
    );

    // If found in context, return it.
    // BUT, if we have an override channel from navigation, inject it here to prevent UI flicker
    if (fromContext) {
      if (location.state?.activeChannelOverride) {
        return {
          ...fromContext,
          activeInfluencerChannel: location.state.activeChannelOverride,
        };
      }
      return fromContext;
    }

    // If NOT in context (e.g. first load, missing from list), construct a "Stub" from navigation state
    if (location.state?.influencerId === activeinfluencerId) {
      return {
        influencerId: activeinfluencerId,
        name: location.state.influencerName || `ID: ${activeinfluencerId}`,
        activeInfluencerChannel: location.state.activeChannelOverride || null,
        status: 'LOADING...', // Placeholder status while fetch happens
        country: 'Loading...',
        idiom: 'Loading...',
        // Add other safe defaults to prevent crashes
      };
    }

    return null;
  }, [queryResults, activeinfluencerId, location.state]);

  // Set active Influencer to start displaying basic info (thumbnail, name, etc.)
  const activeChannel = useMemo(() => {
    return (
      queryResults &&
      queryResults.find((item) => item.influencerId === activeinfluencerId)
    );
  }, [queryResults, activeinfluencerId]);

  console.log('Publications from context =>', publications);

  // useEffext to load page data for first rendering or moving to another influencer
  useEffect(() => {
    const loadPageData = async () => {
      if (!activeinfluencerId) {
        setPublications([]);
        setProductToggles([]); // Also clear products
        setForceReanalysis(false);
        clearAggregatedData();
        setIsLoadingPage(false);
        return;
      }

      if (publications.length === 0) {
        setIsLoadingPage(true);
      }

      try {
        // Fetch ALL necessary data for this page concurrently
        const [
          publicationsResult,
          productsResult,
          marketFitResult,
          summaryResult,
        ] = await Promise.allSettled([
          fetchPublicationsByInfluencer(activeinfluencerId),
          fetchProductsToPublish(activeinfluencerId), // <-- ADDED
          fetchAggregatedMarketFit(activeinfluencerId),
          fetchAggregatedPublicationSummary(activeinfluencerId),
        ]);

        // --- Process Publications --- (Same as before)
        if (
          publicationsResult.status === 'fulfilled' &&
          publicationsResult.value.status
        ) {
          setPublications(publicationsResult.value.data || []);
        } else {
          const errorMsg =
            publicationsResult.value?.message ||
            publicationsResult.reason?.message;
          setError(errorMsg || 'Failed to load publications.');
          setPublications([]);
        }

        // --- Process Products (NOW USING THE SAME CORRECT LOGIC) ---
        if (
          productsResult.status === 'fulfilled' &&
          productsResult.value.status
        ) {
          const productsData = productsResult.value.data; // Get data from the .data property
          const initialToggles = (productsData || []).map((p) => ({
            productId: p.productId,
            productName: p.productName,
            isSelected: false,
          }));
          setProductToggles(initialToggles);
        } else {
          // This now correctly handles both network errors (reason.message)
          // and API business logic errors (value.message).
          const errorMsg =
            productsResult.value?.message || productsResult.reason?.message;
          setError(errorMsg || 'Failed to retrieve product list.');
          setProductToggles([]);
        }

        // --- Aggregated data processing remains the same ---
        // (They update context directly, we just log errors here)
        if (marketFitResult.status === 'rejected') {
          console.error(
            'Failed to fetch aggregated market fit:',
            marketFitResult.reason,
          );
        }
        if (summaryResult.status === 'rejected') {
          console.error(
            'Failed to fetch aggregated publication summary:',
            summaryResult.reason,
          );
        }
        console.log(
          'Aggregation Market fit data structute: ',
          aggregatedMarketFit,
        );
        console.log(
          'Aggregation Publication Summary data structute: ',
          aggregatedPublicationSummary,
        );
      } catch (err) {
        setError(
          err.message ||
            'An unexpected error occurred while loading page data.',
        );
      } finally {
        setIsLoadingPage(false);
      }
    };

    const noJobIsActiveForThisInfluencer =
      contentAnalysisJob.influencerId !== activeinfluencerId ||
      (!contentAnalysisJob.jobId &&
        contentAnalysisJob.jobStatus !== 'starting' &&
        contentAnalysisJob.jobStatus !== 'pending');
    // --- FIX END ---

    // --- DEBUG LOGGING (Requested) ---
    console.log('[PageLoader Debug]', {
      activeID: activeinfluencerId,
      jobID: contentAnalysisJob.jobId,
      jobStatus: contentAnalysisJob.jobStatus,
      jobInfluencerID: contentAnalysisJob.influencerId,
      noJobIsActive: noJobIsActiveForThisInfluencer,
      hasPublications: publications.length > 0,
    });

    if (noJobIsActiveForThisInfluencer) {
      loadPageData();
    } else {
      // If a job is active (starting, pending, or running):
      if (publications.length === 0) {
        loadPageData(); // Block UI if we have no data to show yet
      } else {
        setIsLoadingPage(false); // Unlock UI immediately if we have data
      }
    }

    return () => {
      clearAggregatedData();
    };
  }, [
    activeinfluencerId,
    // ... (fetch functions) ...
    fetchProductsToPublish,
    fetchPublicationsByInfluencer,
    setPublications,
    fetchAggregatedMarketFit,
    fetchAggregatedPublicationSummary,
    setError,
    clearAggregatedData,

    // Job Dependencies
    contentAnalysisJob.jobId,
    contentAnalysisJob.influencerId,
    contentAnalysisJob.jobStatus, // <--- CRITICAL ADDITION: Must listen to status changes ('starting')
    publications.length,
  ]);

  // --- NEW: Effect to check for navigation state ---
  // --- NEW: Debugging Log ---
  useEffect(() => {
    console.log('PublicationPage mounted. Location State:', location.state);
  }, [location.state]);

  useEffect(() => {
    // Check if we have data from the Agent
    if (location.state?.agentJobResult || location.state?.errorMessage) {
      const result = location.state.agentJobResult || {};
      const errorMsg = location.state.errorMessage;

      // We construct a state object compatible with JobStatusPanel
      // JobStatusPanel expects: { jobStatus, results: { warnings: [] }, error: { message } }

      const newState = {
        jobStatus: 'finished', // We treat it as finished so it renders the panel
        results: result, // Pass the full result object (containing 'warnings')
        error: errorMsg ? { message: errorMsg } : null,
      };

      // Only set state if there is something worth showing
      const hasWarnings = result.warnings && result.warnings.length > 0;
      if (hasWarnings || errorMsg) {
        console.log('Setting Agent Job Panel State:', newState);
        setAgentJobState(newState);
      }
    }
  }, [location.state]);

  const handleDismissAgentInfo = () => {
    setAgentJobState(null);
    // Optional: Clear location state to prevent reappearance on refresh
    //navigate(location.pathname, { replace: true, state: {} });
  };

  // --- Publication CRUD Handlers ---

  const handleAddPublication = useCallback(async () => {
    if (!newUrl.trim()) {
      toast.warn('Please enter a URL before adding a publication.');
      return;
    }
    setIsSaving(true);
    setSpinnerMessage(
      `Adding publication for ${activeChannel?.name || 'the influencer'}...`,
    );
    try {
      const selectedProducts = productToggles
        .filter((p) => p.isSelected)
        .map((p) => ({ productId: p.productId }));
      const payload = {
        influencerId: activeinfluencerId,
        channel: newChannel,
        url: newUrl,
        products: selectedProducts,
      };

      const response = await createPublication(payload);

      if (response.status) {
        toast.success('Publication added successfully!');
        // Re-fetch the entire list to ensure consistency with the backend.
        const pubsResponse =
          await fetchPublicationsByInfluencer(activeinfluencerId);
        if (pubsResponse.status) {
          setPublications(pubsResponse.data || []);
        }
        // Clear the form on success
        setNewUrl('');
        setProductToggles((prev) =>
          prev.map((p) => ({ ...p, isSelected: false })),
        );
      } else {
        // The component is now responsible for showing the specific error from the API.
        toast.error(response.message || 'Failed to add publication.');
      }
    } catch (err) {
      toast.error('A client-side error occurred. Please try again.');
      console.error('Error in handleAddPublication:', err);
    } finally {
      setIsSaving(false);
      setSpinnerMessage('');
    }
  }, [
    newUrl,
    newChannel,
    productToggles,
    activeinfluencerId,
    activeChannel?.name,
    createPublication,
    fetchPublicationsByInfluencer,
    setPublications,
    setProductToggles,
  ]);

  const handleDeletePublication = useCallback(
    async (publicationId) => {
      if (
        !window.confirm(
          'Are you sure you want to permanently delete this publication?',
        )
      ) {
        return;
      }
      setIsSaving(true);
      setSpinnerMessage(`Deleting publication...`);
      try {
        const response = await deletePublication(publicationId);

        if (response.status) {
          toast.success('Publication deleted successfully.');

          // === THE FIX ===
          // 1. Calculate the new array *before* calling the setter.
          //    The component uses the `publications` state it already has from the context.
          const updatedPublications = publications.filter(
            (p) => p.publicationId !== publicationId,
          );

          // 2. Pass the final, calculated array to the dispatcher.
          setPublications(updatedPublications);
          // === END OF FIX ===
        } else {
          toast.error(response.message || 'Failed to delete publication.');
        }
      } catch (err) {
        toast.error('A client-side error occurred. Please try again.');
        console.error('Error in handleDeletePublication:', err);
      } finally {
        setIsSaving(false);
        setSpinnerMessage('');
      }
      // CRITICAL: The `publications` state variable is now a direct dependency of this function.
    },
    [deletePublication, setPublications, publications],
  );

  const handleRefreshMetadata = useCallback(
    async (publicationId) => {
      toast.info('Refreshing publication data from YouTube...');
      const result = await refreshPublicationMetadata(
        activeinfluencerId,
        publicationId,
      );
      if (result.status) {
        const warnings = result.data?.warnings;
        if (warnings && warnings.length > 0) {
          // --- Case 1: Partial Success (Success with Warnings) ---
          const warningMessage = (
            <div>
              <p>Refresh complete with warnings:</p>
              <ul style={{ textAlign: 'left', margin: 0, paddingLeft: '20px' }}>
                {warnings.map((warn, index) => (
                  <li key={index}>{warn}</li>
                ))}
              </ul>
            </div>
          );
          toast.warn(warningMessage, {
            autoClose: 10000, // Give user more time to read the warnings
          });
        } else {
          // --- Case 2: Full Success ---
          toast.success('Publication data refreshed successfully!');
        }
      } else {
        // --- Case 3: Hard Failure ---
        toast.error(
          result.message ||
            'An unexpected error occurred while refreshing data.',
        );
      }
    },
    [refreshPublicationMetadata],
  );

  // --- Publication Interaction Handlers ---

  const handleToggleProduct = useCallback(
    async (publicationId, productId) => {
      const response = await togglePublishedProduct(publicationId, productId);
      // The DataContext wrapper handles the loading state and success state update.
      // The component's only job is to show a toast on failure.
      if (!response.status) {
        toast.error(response.message || 'Failed to update product selection.');
      }
    },
    [togglePublishedProduct],
  );

  const handleToggleIncludeVideoData = useCallback(
    async (publicationId) => {
      const response = await toggleIncludeVideoData(publicationId);
      // The DataContext wrapper handles the loading state and success state update.
      // The component's only job is to show a toast on failure.
      if (!response.status) {
        toast.error(response.message || 'Failed to update video setting.');
      }
    },
    [toggleIncludeVideoData],
  );

  const handleNewProductToggle = useCallback(
    (productId) => {
      setProductToggles((prevToggles) =>
        prevToggles.map((product) =>
          product.productId === productId
            ? { ...product, isSelected: !product.isSelected }
            : product,
        ),
      );
    },
    [setProductToggles],
  );

  // --- AI Summary & Analysis Handlers ---

  // --- 4. REWIRED AI & ANALYSIS HANDLERS ---
  const handleGenerateOrRegenerateSummary = useCallback(
    async (publication) => {
      if (!publication?.videoUrl) {
        toast.warn('Video URL is missing.');
        return;
      }
      if (
        publication.hasAiSummary &&
        !window.confirm(
          'Regenerate summary? This will overwrite the existing one.',
        )
      )
        return;

      toast.info('Starting in-depth analysis...');
      const response = await generatePublicationAISummary({
        publicationId: publication.publicationId,
        videoUrl: publication.videoUrl,
      });

      if (response.status) {
        toast.success('Analysis complete!');
        // Set the raw data and open the correct modal
        setSelectedContentQualityData(response.data.aiSummary);
        setIsContentQualityModalOpen(true);
      } else {
        toast.error(response.message || 'Failed to generate analysis.');
      }
    },
    [generatePublicationAISummary],
  );

  const handleGeneratePrivateSummary = useCallback(
    async (publication) => {
      if (!publication?.videoUrl) {
        toast.warn('Video URL is missing.');
        return;
      }
      if (
        !window.confirm('This will start a private video analysis. Continue?')
      )
        return;

      toast.info('Starting private video analysis...');
      const response = await generatePrivatePublicationAISummary({
        publicationId: publication.publicationId,
        videoUrl: publication.videoUrl,
      });

      if (response.status) {
        toast.success('Private video analysis complete!');
        // Set the raw data and open the correct modal
        setSelectedContentQualityData(response.data.aiSummary);
        setIsContentQualityModalOpen(true);
      } else {
        toast.error(response.message || 'Failed to analyze private video.');
      }
    },
    [generatePrivatePublicationAISummary],
  );

  // This handler is now ONLY for INDIVIDUAL publications
  const handleViewSummary = useCallback(
    async (publication) => {
      let summaryContent = publication.aiSummary;
      if (publication.hasAiSummary && !summaryContent) {
        const fetchToastId = toast.info('Retrieving analysis...');
        const result = await fetchPublicationAiSummary(
          publication.publicationId,
        );
        toast.dismiss(fetchToastId);
        if (result.status) {
          summaryContent = result.data.aiSummary;
          console.log('Ai Summary Data Structure:', summaryContent);
        } else {
          toast.error(result.message || 'Failed to fetch summary details.');
          return;
        }
      } else if (!publication.hasAiSummary) {
        toast.error(
          "There is no content quality analysis for this publication. Please click on 'Generate Deep Content Analysis' for a new analysis",
        );
      } else {
        console.log(
          '[DEBUG 3. State Setter] Data OBJECT being passed to setSelectedContentQualityData:',
          summaryContent,
        );
        setSelectedContentQualityData(summaryContent);
        setIsContentQualityModalOpen(true);
      } // Toggles the INDIVIDUAL modal
    },
    [fetchPublicationAiSummary],
  );

  // --- Market Fit & Job Handlers ---

  // src/components/PublicationPage.js

  const handleViewMarketFit = useCallback(
    async (publication) => {
      const cachedData = individualMarketFitAnalysis[publication.videoId];
      console.log('Market Fit cached data:', cachedData);

      if (cachedData) {
        // --- THE FIX: Unwrap the data if it comes from the Job Result/Cache structure ---
        const dataToDisplay = cachedData.analysis
          ? cachedData.analysis
          : cachedData;
        setSelectedMarketFitData(dataToDisplay);
        setIsMarketFitModalOpen(true);
        return;
      }

      const fetchToastId = toast.info('Fetching analysis from database...');
      const result = await fetchIndividualMarketFitAnalysis(publication);
      toast.dismiss(fetchToastId);

      if (result.status) {
        // Ensure we also handle potential nesting from the DB fetch, though usually not needed
        const dataToDisplay = result.data?.analysis
          ? result.data.analysis
          : result.data;
        setSelectedMarketFitData(dataToDisplay);
        setIsMarketFitModalOpen(true);
      } else {
        toast.error(result.message || 'Could not fetch analysis.');
      }
    },
    [individualMarketFitAnalysis, fetchIndividualMarketFitAnalysis],
  );

  const handleReanalyzeMarketFit = useCallback(
    async (publication) => {
      toast.info('Re-analyzing market fit for a single video...');
      const result = await generateSingleMarketFitAnalysis({
        publicationId: publication.publicationId,
        videoUrl: publication.videoUrl,
      });
      if (result.status) {
        toast.success('Re-analysis complete and data updated.');
        // Update the local state to show the new analysis immediately
        setSelectedMarketFitData(result.data);
        setIsMarketFitModalOpen(true);
      } else {
        toast.error(result.message || 'Failed to re-analyze market fit.');
      }
    },
    [generateSingleMarketFitAnalysis],
  );

  const handleStartDeepAnalysis = useCallback(async () => {
    if (!activeinfluencerId || publications.length === 0) {
      toast.warn('There are no publications to analyze for this influencer.');
      return;
    }
    if (
      !window.confirm(
        `This will start a deep analysis of all ${publications.length} publications. This may take several minutes. Continue?`,
      )
    ) {
      return;
    }
    const publicationsToAnalyze = publications
      .map((pub) => ({
        videoId: pub.videoId,
        videoUrl: pub.videoUrl, // The full URL is needed for yt-dlp
        channel: pub.channel, // The channel is needed for the backend strategy
      }))
      .filter((pub) => pub.videoId && pub.videoUrl && pub.channel); // Ensure all required fields are present

    if (publicationsToAnalyze.length === 0) {
      toast.error(
        'None of the publications have the required information (Video ID, URL, Channel) for analysis. Please include them and try again.',
      );
      return;
    }
    // --- END REFACTORED PART ---

    clearContentAnalysisJobState();
    setContentAnalysisJobState({
      jobStatus: 'starting',
      error: null,
      results: null,
      influencerId: activeinfluencerId,
    });

    const result = await startContentAnalysisJob(
      activeinfluencerId,
      publicationsToAnalyze, // <-- The new payload is passed here
      forceReanalysis,
    );
    if (result.status) {
      setContentAnalysisJobState({
        jobId: result.data.jobId,
        jobStatus: 'pending',
        message: 'Job submitted. Waiting for progress updates...',
      });
      toast.info('Deep analysis job started successfully!');
    } else {
      const errorMsg = result.message || 'Could not start the analysis job.';
      setContentAnalysisJobState({
        jobStatus: 'failed',
        error: { message: errorMsg },
      });
      toast.error(errorMsg);
    }
  }, [
    activeinfluencerId,
    publications,
    forceReanalysis,
    startContentAnalysisJob,
    setContentAnalysisJobState,
    clearContentAnalysisJobState,
  ]);

  if (isLoadingQueryData && !activeInfluencer) {
    return <Spinner message="Loading publication details..." />;
  }

  // 2. If not loading, but still missing (e.g. fetch failed), show error/empty.
  if (!activeInfluencer && activeinfluencerId && !isLoadingQueryData) {
    return (
      <div className="publication-page">
        <p className="noData">
          Could not load data for influencer ID {activeinfluencerId}. Please try
          refreshing the page.
        </p>
      </div>
    );
  }

  // 10) If no active Influencer ask user to run a query
  if (!queryResults || !queryResults.length || !activeinfluencerId) {
    return (
      <p className="noData">
        No data available. Please run a query to select influencers to work
        with.
      </p>
    );
  }

  const currentIndex = queryResults.findIndex(
    (item) => item.influencerId === activeinfluencerId,
  );

  const isJobForCurrentInfluencer =
    contentAnalysisJob.influencerId === activeinfluencerId;

  const isJobRunning =
    isJobForCurrentInfluencer &&
    (contentAnalysisJob.jobStatus === 'pending' ||
      contentAnalysisJob.jobStatus === 'starting');

  const isLocked = isSaving || isJobRunning;

  // DEBUG: Find out who is blocking the UI
  console.log('[UI LOCK DEBUG]', {
    isSaving,
    isLoadingPage, // Local state
    jobStatus: contentAnalysisJob?.jobStatus,
    jobId: contentAnalysisJob?.jobId,
  });

  return (
    <div className="publication-page">
      {/* Fixed Header: influencer info + navigation + Publications title */}
      <div className="fixed-header">
        <div className="header-container">
          <div className="header-group">
            <img
              src={activeChannel?.thumbnail}
              alt={`${activeChannel?.name} Thumbnail`}
              className="thumbnail"
            />
          </div>
          <div className="header-group">
            <h2>{activeChannel?.name}</h2>
            <p>
              Status: <span>{activeChannel?.status}</span>
            </p>
          </div>
          <div className="header-group">
            <p>
              Country: <span>{activeChannel?.country || 'N/A'}</span>
            </p>
            <p>
              Idiom: <span>{activeChannel?.idiom || 'N/A'}</span>
            </p>
          </div>
          <div className="header-group">
            <p>
              Nickname: <span>{activeChannel?.nickname || 'N/A'}</span>
            </p>
            <p>
              Last Contact:{' '}
              <span>
                {new Date(activeChannel?.dateLastContact).toLocaleString([], {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                }) || 'N/A'}
              </span>
            </p>
          </div>
          <div className="navigation">
            <button
              onClick={() => {
                if (currentIndex > 0) {
                  setActiveinfluencerId(
                    queryResults[currentIndex - 1].influencerId,
                  );
                }
              }}
              disabled={currentIndex === 0}
            >
              Previous
            </button>
            <span>
              {currentIndex + 1}/{queryResults.length}
            </span>
            <button
              onClick={() => {
                if (currentIndex < queryResults.length - 1) {
                  setActiveinfluencerId(
                    queryResults[currentIndex + 1].influencerId,
                  );
                }
              }}
              disabled={currentIndex === queryResults.length - 1}
            >
              Next
            </button>
          </div>
        </div>
        {/* --- HIGHLIGHT: New Deep Analysis Button and Status Section --- */}
        <div className="deep-analysis-section">
          <button
            onClick={handleStartDeepAnalysis}
            className="deep-analysis-button"
            disabled={
              contentAnalysisJob.jobStatus === 'pending' ||
              contentAnalysisJob.jobStatus === 'starting'
            }
          >
            {contentAnalysisJob.jobStatus === 'pending' ||
            contentAnalysisJob.jobStatus === 'starting'
              ? 'Analysis in Progress...'
              : 'Generate Deep Content Analysis'}
          </button>
          <div className="force-reanalysis-and-date-container">
            <div className="latest-assessment-date">
              {(() => {
                const marketFitDateStr = aggregatedMarketFit?.assessment_date;
                const publicationDateStr =
                  aggregatedPublicationSummary?.assessment_date;

                let latestDateStr = 'N/A';

                // Create an array of valid date strings
                const dateStrings = [
                  marketFitDateStr,
                  publicationDateStr,
                ].filter(Boolean);

                if (dateStrings.length > 0) {
                  // --- THIS IS THE CORRECTED LOGIC ---

                  // 1. Map the date strings to numeric timestamps (milliseconds)
                  const timestamps = dateStrings.map((dateStr) => {
                    // new Date() is safe here because the backend sends a full ISO string now
                    return new Date(dateStr).getTime();
                  });

                  // 2. Filter out any potential invalid dates which would result in NaN
                  const validTimestamps = timestamps.filter((ts) => !isNaN(ts));

                  if (validTimestamps.length > 0) {
                    // 3. Find the maximum timestamp (which is just a number)
                    const maxTimestamp = Math.max(...validTimestamps);

                    // 4. Create a single new Date object from the latest timestamp
                    const latestDate = new Date(maxTimestamp);

                    // 5. Format the final, correct date for display
                    latestDateStr = latestDate.toLocaleDateString([], {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    });
                  }
                  // --- END OF CORRECTION ---
                }

                return (
                  <p>
                    Last Analyzed on: <span>{latestDateStr}</span>{' '}
                  </p>
                );
              })()}
            </div>
            {contentAnalysisJob.jobStatus !== 'pending' &&
              contentAnalysisJob.jobStatus !== 'starting' && (
                <div
                  className="force-reanalysis-container"
                  title="Ignores all cached data and re-analyzes videos with the latest AI logic. This will take longer and incur full costs."
                >
                  <label>
                    <input
                      type="checkbox"
                      checked={forceReanalysis}
                      onChange={(e) => setForceReanalysis(e.target.checked)}
                    />
                    <span>Force Re-analysis</span>
                  </label>
                </div>
              )}
          </div>
          {/* --- REFACTORED: Centralized status/error rendering --- */}
          {isJobForCurrentInfluencer && (
            <JobStatusPanel
              jobState={contentAnalysisJob}
              onDismissError={clearContentAnalysisJobError}
              onCancel={cancelContentAnalysisJob} // <--- Pass it here
            />
          )}
          {/* --- NEW AGENT RESULT PANEL --- */}
          {!isJobForCurrentInfluencer && agentJobState && (
            <JobStatusPanel
              jobState={agentJobState}
              onDismissError={handleDismissAgentInfo}
            />
          )}
        </div>
      </div>

      <div className="publications-scroll-container">
        {/* --- AGGREGATED RESULTS SECTION --- */}
        {(aggregatedMarketFit || aggregatedPublicationSummary) && (
          <div className="aggregated-results-container">
            {aggregatedMarketFit && (
              <div className="aggregated-card market-fit">
                <h3>Aggregated Content Fit</h3>
                <span
                  className={`tierBadge ${getTierClassName(aggregatedMarketFit.marketFitTier)}`}
                >
                  {aggregatedMarketFit.marketFitTier}
                </span>
                <p>
                  <strong>Overall Score:</strong>{' '}
                  {aggregatedMarketFit.marketFitScore?.toFixed(2)}
                </p>
                <ReactMarkdown>
                  {formatMarkdownContent(aggregatedMarketFit.rationale)}
                </ReactMarkdown>
                <button
                  className="dashboard-button"
                  onClick={() => setIsAggregatedMarketFitModalOpen(true)}
                  disabled={!aggregatedMarketFit}
                >
                  View Dashboard
                </button>
              </div>
            )}
            {aggregatedPublicationSummary && (
              <div className="aggregated-card publication-quality">
                <h3>Aggregated Content Quality</h3>
                <span
                  className={`tierBadge ${getTierClassName(aggregatedPublicationSummary.tier)}`}
                >
                  {aggregatedPublicationSummary.tier}
                </span>
                <p>
                  <strong>Overall Score:</strong>{' '}
                  {aggregatedPublicationSummary.total_weighted_score?.toFixed(
                    2,
                  )}
                </p>
                <ReactMarkdown>
                  {formatMarkdownContent(
                    aggregatedPublicationSummary.conclusion,
                  )}
                </ReactMarkdown>
                <button
                  className="dashboard-button"
                  onClick={() => {
                    setSelectedContentQualityData(aggregatedPublicationSummary);
                    setIsContentQualityModalOpen(true);
                  }}
                  disabled={!aggregatedPublicationSummary}
                >
                  View Dashboard
                </button>
              </div>
            )}
          </div>
        )}
        <h2 className="publication-page-title">Publications</h2>
        <div className="publications-container">
          {(!publications || publications.length === 0) &&
            !isSaving &&
            !isLoadingPage && <p>No publications yet. Add one below.</p>}

          {(isSaving || isLoadingPage) && <Spinner message={spinnerMessage} />}
          {error && <ErrorDisplayModal error={error} clearError={clearError} />}
          {errorMessage && !isSaving && (
            <p className="error-message">{errorMessage}</p>
          )}

          {Array.isArray(publications) &&
            publications.map((pub) => {
              console.log(
                "[DEBUG 4. Data Source] Full 'pub' object in .map() loop:",
                pub,
              );
              const channelKey = pub.channel.toLowerCase();
              const isYoutubePublic =
                channelKey === 'youtube_public' ||
                channelKey === 'youtube public' ||
                channelKey === 'youtube';
              const isYoutubePrivate = channelKey === 'youtube_private';
              const isInstagram =
                channelKey === 'instagram' || channelKey === 'ig';
              const isTiktok = channelKey === 'tiktok';
              const useRichLayout =
                isYoutubePublic || isYoutubePrivate || isInstagram || isTiktok;

              if (useRichLayout) {
                return (
                  <div key={pub.publicationId}>
                    <div
                      className={`youtube-insta-layout publication-item ${isYoutubePrivate ? 'private-video-item' : ''}`}
                    >
                      <div className="thumbnail-column">
                        <div className="video-toggle-container">
                          <input
                            type="checkbox"
                            className="video-toggle-checkbox"
                            checked={!!pub.includeVideoData}
                            disabled={pub.isTogglingVideo || isLocked} // <--- DISABLE
                            onChange={() =>
                              handleToggleIncludeVideoData(pub.publicationId)
                            }
                          />
                          {pub.isTogglingVideo && (
                            <span className="mini-spinner">
                              <Spinner />
                            </span>
                          )}
                        </div>
                        {pub.videoUrl && (
                          <a
                            href={formatUrlForHref(pub.videoUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {pub.thumbnailUrl ? (
                              <img
                                src={pub.thumbnailUrl}
                                alt="Video Thumbnail for Analysis"
                                className="publication-thumbnail"
                              />
                            ) : (
                              <div className="publication-thumbnail fallback-thumbnail">
                                View on Platform
                              </div>
                            )}
                          </a>
                        )}
                      </div>

                      <div className="title-row">
                        <p>
                          Title:
                          <span>
                            {' '}
                            {pub.titleVideo || 'Title not available'}
                          </span>
                          {isYoutubePrivate && (
                            <span className="private-video-badge">
                              Private Video
                            </span>
                          )}
                        </p>
                      </div>

                      <div className="published-row">
                        <p>
                          Published:
                          <span>
                            {' '}
                            {isYoutubePrivate
                              ? 'Unpublished (Draft)'
                              : new Date(pub.publishedAt).toLocaleTimeString(
                                  [],
                                  {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  },
                                ) || 'N/A '}
                          </span>
                          . Duration:
                          <span> {formatDuration(pub.durationSeconds)} </span>.
                          Last Updated on:{' '}
                          <span>
                            {' '}
                            {(pub.lastApiSyncAt &&
                              new Date(pub.lastApiSyncAt).toLocaleDateString(
                                [],
                                {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                },
                              )) ||
                              'N/A'}
                          </span>
                          .
                        </p>
                      </div>
                      <div className="refresh-row">
                        <button
                          onClick={() =>
                            handleRefreshMetadata(pub.publicationId)
                          }
                          className="refresh-metadata-btn refresh-row"
                          disabled={
                            pub.isRefreshing || !isYoutubePublic || isLocked
                          }
                          title={
                            !isYoutubePublic
                              ? 'Refresh is only available for public YouTube publications'
                              : 'Refresh YouTube Metadata'
                          }
                        >
                          <FontAwesomeIcon
                            icon={faSync}
                            spin={pub.isRefreshing}
                          />
                          {pub.isRefreshing ? 'Refreshing...' : 'Refresh'}
                        </button>
                      </div>

                      <div className="channel-likes-row">
                        <p>
                          Channel:<span> {pub.channel}</span>
                        </p>
                        <p>
                          Likes:
                          <span>
                            {' '}
                            {isYoutubePrivate
                              ? 'N/A'
                              : (pub.likeCount?.toLocaleString() ?? 'N/A')}
                          </span>
                        </p>
                      </div>

                      <button
                        onClick={() =>
                          handleDeletePublication(pub.publicationId)
                        }
                        className="delete-publication-button grid-delete-btn"
                        disabled={isLocked} // <--- DISABLE
                        title={
                          isJobRunning
                            ? 'Cannot delete while analysis is running'
                            : 'Delete'
                        }
                      >
                        Delete
                      </button>
                      <div className="views-comments-row">
                        <p>
                          Views:
                          <span>
                            {' '}
                            {isYoutubePrivate
                              ? 'N/A'
                              : (pub.viewCount?.toLocaleString() ?? 'N/A')}
                          </span>
                        </p>
                        <p>
                          Comments:
                          <span>
                            {' '}
                            {isYoutubePrivate
                              ? 'N/A'
                              : (pub.commentCount?.toLocaleString() ?? 'N/A')}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="products-published-title">
                      Products published:
                    </div>
                    <div className="product-buttons">
                      {pub.products && pub.products.length > 0 ? (
                        pub.products.map((prod) => (
                          <button
                            key={prod.productId}
                            className={`product-toggle-button ...`}
                            disabled={
                              pub.isTogglingProduct === prod.productId ||
                              isLocked
                            } // <--- DISABLE
                            onClick={() =>
                              handleToggleProduct(
                                pub.publicationId,
                                prod.productId,
                              )
                            }
                          >
                            {pub.isTogglingProduct === prod.productId ? (
                              <span className="mini-spinner-inline">
                                <Spinner size="small" />
                              </span>
                            ) : (
                              prod.productName
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="no-products-message">
                          No products published.
                        </p>
                      )}
                    </div>

                    <div className="ai-summary-section">
                      <h5 className="ai-summary-title">AI-Powered Analysis</h5>
                      {pub.analysisError ? (
                        <div className="analysis-error-notice">
                          <p>
                            <strong>Analysis Failed:</strong>{' '}
                            {pub.analysisError}
                          </p>
                        </div>
                      ) : pub.videoUrl ? (
                        <div className="ai-summary-actions four-button-layout">
                          <button
                            onClick={() => handleViewMarketFit(pub)}
                            className="ai-summary-button view-fit"
                          >
                            View Content Fit
                          </button>
                          <button
                            onClick={() => handleViewSummary(pub)}
                            className="ai-summary-button view-quality"
                          >
                            View Content Quality
                          </button>
                        </div>
                      ) : (
                        <p className="ai-summary-notice">
                          <i>Video URL required for analysis.</i>
                        </p>
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div
                    key={pub.publicationId}
                    className="simple-layout publication-item"
                  >
                    <div className="simple-layout-header">
                      <input
                        type="checkbox"
                        className="video-toggle-checkbox"
                        checked={!!pub.includeVideoData}
                        disabled={pub.isTogglingVideo || isSaving}
                        onChange={() =>
                          handleToggleIncludeVideoData(pub.publicationId)
                        }
                      />
                      {pub.isTogglingVideo && (
                        <span className="mini-spinner">
                          <Spinner />
                        </span>
                      )}
                    </div>
                    <div className="simple-row">
                      <p>
                        <strong>Channel:</strong>
                        <span> {pub.channel}</span>
                      </p>
                      <p>
                        <strong>Media link:</strong>{' '}
                        <a
                          href={formatUrlForHref(pub.videoUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {pub.videoUrl || 'N/A'}
                        </a>
                      </p>
                      <button
                        onClick={() =>
                          handleDeletePublication(pub.publicationId)
                        }
                        className="delete-publication-button"
                        disabled={isSaving}
                      >
                        Delete
                      </button>
                    </div>
                    <div className="products-published-title">
                      Products published:
                    </div>
                    <div className="product-buttons">
                      {pub.products && pub.products.length > 0 ? (
                        pub.products.map((prod) => (
                          <button
                            key={prod.productId}
                            className={`product-toggle-button ${prod.isSelected ? 'selected' : 'deselected'}`}
                            disabled={pub.isTogglingProduct === prod.productId}
                            onClick={() =>
                              handleToggleProduct(
                                pub.publicationId,
                                prod.productId,
                              )
                            }
                          >
                            {pub.isTogglingProduct === prod.productId ? (
                              <span className="mini-spinner-inline">
                                <Spinner size="small" />
                              </span>
                            ) : (
                              prod.productName
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="no-products-message">
                          No products published.
                        </p>
                      )}
                    </div>
                  </div>
                );
              }
            })}

          <div className="add-publication-form">
            <div className="add-publication-row1">
              <h4 className="centered-title">Add a New Publication:</h4>
              <div className="form-field">
                <label htmlFor="channel">Channel:</label>
                <select
                  id="channel"
                  value={newChannel}
                  onChange={(e) => setNewChannel(e.target.value)}
                  disabled={isLocked}
                >
                  {/* --- MODIFIED: Conditionally disable options --- */}
                  {CHANNEL_OPTIONS.map((media) => {
                    const isUnavailable = UNIMPLEMENTED_PLATFORMS.includes(
                      media.key,
                    );
                    return (
                      <option
                        key={media.key}
                        value={media.key}
                        disabled={isUnavailable}
                      >
                        {isUnavailable
                          ? `${media.label} (Coming Soon)`
                          : media.label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="url">URL:</label>
                <input
                  id="url"
                  type="text"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="Enter link"
                  disabled={isLocked}
                />
              </div>
            </div>
            <div className="add-publication-row2">
              <div className="products-title">Products:</div>
              <div className="product-toggle-container">
                {productToggles.length > 0 ? (
                  productToggles.map((prod) => (
                    <button
                      key={prod.productId}
                      className={`product-toggle-button ${prod.isSelected ? 'selected' : 'deselected'}`}
                      onClick={() => handleNewProductToggle(prod.productId)}
                      disabled={isLocked}
                    >
                      {prod.productName}
                    </button>
                  ))
                ) : (
                  <p className="no-products-message">
                    No products available to add.
                  </p>
                )}
              </div>
            </div>
            <div className="add-publication-row3">
              {/* Change label to explain why it is disabled if running */}
              <button onClick={handleAddPublication} disabled={isLocked}>
                {isJobRunning
                  ? 'Analysis Running...'
                  : isSaving
                    ? 'Adding...'
                    : 'Add Publication'}
              </button>
            </div>
          </div>

          {isMarketFitModalOpen && selectedMarketFitData && (
            <MarketFitDashboardModal
              isOpen={isMarketFitModalOpen}
              onClose={() => setIsMarketFitModalOpen(false)}
              data={selectedMarketFitData}
              isLoadingPage={false}
              tierClassName={selectedMarketFitData.marketFitTier?.replace(
                /\s+/g,
                '',
              )}
            />
          )}
          {isAggregatedMarketFitModalOpen && aggregatedMarketFit && (
            <MarketFitDashboardModal
              isOpen={isAggregatedMarketFitModalOpen}
              onClose={() => setIsAggregatedMarketFitModalOpen(false)}
              data={aggregatedMarketFit}
              isLoadingPage={false}
              tierClassName={aggregatedMarketFit.marketFitTier?.replace(
                /\s+/g,
                '',
              )}
            />
          )}
          {console.log(
            '[DEBUG 2. Parent State] Value of selectedContentQualityData before modal render:',
            selectedContentQualityData,
          )}
          {/* Modal for INDIVIDUAL publication quality */}
          {isContentQualityModalOpen && selectedContentQualityData && (
            <ContentQualityDashboardModal
              isOpen={isContentQualityModalOpen}
              onClose={() => setIsContentQualityModalOpen(false)}
              data={selectedContentQualityData}
            />
          )}

          {/* Modal for AGGREGATED publication quality */}
          {isAggregatedContentQualityModalOpen &&
            aggregatedPublicationSummary && (
              <ContentQualityDashboardModal
                isOpen={isAggregatedContentQualityModalOpen}
                onClose={() => setIsAggregatedContentQualityModalOpen(false)}
                data={aggregatedPublicationSummary} // Directly uses the context data
              />
            )}
        </div>
      </div>
    </div>
  );
};
export default PublicationPage;
// --- End Updated Code for PublicationPage.js ---
